import {
	CompanyLabelType,
	SecondaryCompanyLabelType,
	TestHierarchyNodeType,
} from "node-library/dist/constants";
import { areStringArraysEqual } from "node-library/dist/helpers/misc_helper";
import {
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestLabelHierarchyNodeMappingPackage,
	FrontendCompanyTestLabels,
	FrontendSampleInstance,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";

type LabelInputKey = `${string}-${string}-${string}`;
// Format: `${sampleInstanceId}-${labelId}-${packageId}`

export type LabelInputState = {
	[key in LabelInputKey]: string;
};
export function getLabelInputKey(
	sampleInstanceId: string,
	labelId: string,
	secondaryLabelId: string,
	packageId: string
): LabelInputKey {
	return `${sampleInstanceId}-${labelId}-${secondaryLabelId}-${packageId}`;
}

export function hasLabelPackageMatchingSampleInstance(
	companyTestLabels: Array<FrontendCompanyTestLabels>,
	sampleInstances: Array<FrontendSampleInstance>
): boolean {
	for (const instance of sampleInstances) {
		const selectedNodeIds = new Set(
			instance.sampleTestInfo?.selectedTests.map(
				(test) => test.selectedNodeId
			) ?? []
		);

		// Skip if no selected nodes
		if (selectedNodeIds.size === 0) continue;

		for (const label of companyTestLabels) {
			for (const pkg of label.nodeMappingPackages) {
				const allMatch = pkg.mappingNodeIds.every((nodeId) =>
					selectedNodeIds.has(nodeId)
				);

				if (allMatch) {
					return true;
				}
			}
		}
	}

	return false;
}

function collectNodeIdsFromTree(tree: TestHierarchyTreeNode): Set<string> {
	const ids = new Set<string>();

	function traverse(node: TestHierarchyTreeNode) {
		ids.add(node.nodeId);
		node.children.forEach(traverse);
	}

	traverse(tree);
	return ids;
}

export function getMatchingLabelsAndPackagesFromHierarchyTree(
	companyTestLabels: FrontendCompanyTestLabels[],
	instanceHierarchyTree: TestHierarchyTreeNode,
	unfilteredHierarchyTree: TestHierarchyTreeNode
): Array<{
	label: FrontendCompanyTestLabels;
	matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
}> {
	const selectedNodeIds = collectNodeIdsFromTree(instanceHierarchyTree);
	const matchingLabels: Array<{
		label: FrontendCompanyTestLabels;
		matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
	}> = [];

	for (const label of companyTestLabels) {
		if (label.labelType === CompanyLabelType.X_TYPE_SAMPLE_ID_LABEL) {
			//check if multiple test groups are enabled
			const instanceTestGroups: Array<TestHierarchyTreeNode> =
				unfilteredHierarchyTree.children.filter((child) => {
					return child.nodeType === TestHierarchyNodeType.TEST_GROUP;
				});
			if (instanceTestGroups.length > 1) {
				matchingLabels.push({ label, matchingPackages: [] });
			}
		} else {
			const matchedPackages = label.nodeMappingPackages.filter((pkg) =>
				pkg.mappingNodeIds.every((nodeId) => selectedNodeIds.has(nodeId))
			);

			if (matchedPackages.length > 0) {
				matchingLabels.push({
					label,
					matchingPackages: matchedPackages,
				});
			}
		}
	}

	return matchingLabels;
}

export function getLabelsRequiringUserInput(
	instanceLabelsToPrint: Array<FrontendCompanyTestLabels>
): FrontendCompanyTestLabels[] {
	return instanceLabelsToPrint.filter((label) =>
		label.secondaryLabels.some(
			(sec) =>
				sec.secondaryLabelType === SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC
		)
	);
}
